<!--
  "הוסיפו את PlanUp למסך הבית" - the small popup a phone / tablet gets after login (a bottom sheet
  on a phone, a small card elsewhere). Mounted once in App.vue for logged-in business users; a few
  seconds after the app is up, when nothing else is open and the user paused, it shows - unless the
  device already has the app (opened from the icon, or added before), said "לא להציג שוב", or said
  "לא עכשיו" in the last week (modules/HomeScreenInstall.js keeps that per device).

  "הוספה למסך הבית": Chrome / Edge / Samsung on Android open their own install dialog (one tap);
  iPhone / iPad and browsers without that dialog get the steps (share button → הוספה למסך הבית).
  On Android the popup waits a few seconds for the browser's dialog to become available.

  The settings section (HomeScreenSettings.vue) opens it with $root 'openHomeScreenPrompt' - then
  on any device, and closing it records nothing.

  Not for the root admin, the reception-desk kiosk, a frozen account, or a new account's first load
  (that one belongs to the starter kit popup).
-->
<template>

    <div v-if="open"
         class="homeScreenPrompt"
         :class="{ leaving: leaving }"
         @mousedown="onBackdropMousedown"
         @click.self="onBackdropClick">

        <div ref="card"
             class="homeScreenCard"
             role="dialog"
             aria-modal="true"
             aria-labelledby="homeScreenTitle"
             tabindex="-1"
             :data-mode="mode"
             :data-platform="platform">

            <button type="button"
                    class="homeScreenClose"
                    aria-label="סגירה"
                    title="סגירה"
                    @click="notNow">
                <i class="fa-solid fa-xmark"></i>
            </button>

            <div class="homeScreenHead">
                <img class="homeScreenAppIcon"
                     src="/static/pwa/apple-touch-icon.png"
                     alt="">
                <div class="homeScreenHeadText">
                    <div id="homeScreenTitle"
                         class="homeScreenTitle">הוסיפו את PlanUp למסך הבית</div>
                    <div class="homeScreenSub">כניסה בלחיצה אחת, במסך מלא - כמו אפליקציה.</div>
                </div>
            </div>

            <!-- ── the offer ── -->
            <template v-if="mode == 'intro'">

                <div class="homeScreenActions">
                    <button type="button"
                            class="btn btn-primary homeScreenAdd"
                            :disabled="installing"
                            @click="add">
                        <i :class="installing ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-mobile-screen-button'"></i>
                        הוספה למסך הבית
                    </button>
                    <button type="button"
                            class="btn btn-light homeScreenNotNow"
                            @click="notNow">לא עכשיו</button>
                </div>

                <div v-if="!forced"
                     class="homeScreenFoot">
                    <a href="#"
                       class="homeScreenNever"
                       @click.prevent="never">לא להציג שוב</a>
                    <span v-if="canSeeSettings"
                          class="homeScreenSettingsHint">· אפשר להוסיף בכל זמן מההגדרות</span>
                </div>

            </template>

            <!-- ── how, on this device ── -->
            <template v-else>

                <ol class="homeScreenSteps">
                    <li v-for="(step, i) in steps"
                        :key="i"
                        class="homeScreenStep">
                        <span class="homeScreenStepNum">{{ i + 1 }}</span>
                        <span class="homeScreenStepText">
                            <span class="homeScreenStepTitle">
                                {{ step.title }}
                                <i v-if="step.icon"
                                   class="homeScreenStepIcon"
                                   :class="step.icon"></i>
                            </span>
                            <span v-if="step.sub"
                                  class="homeScreenStepSub">{{ step.sub }}</span>
                        </span>
                    </li>
                </ol>

                <div class="homeScreenActions">
                    <!-- the browser's one-tap dialog became available while the steps were open -->
                    <button v-if="state.canPrompt"
                            type="button"
                            class="btn btn-primary homeScreenAdd"
                            :disabled="installing"
                            @click="add">
                        <i :class="installing ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-mobile-screen-button'"></i>
                        הוספה בלחיצה אחת
                    </button>
                    <button v-else-if="platform != 'desktop'"
                            type="button"
                            class="btn btn-primary homeScreenDone"
                            @click="done">
                        <i class="fa-solid fa-check"></i>
                        הוספתי
                    </button>
                    <button type="button"
                            class="btn homeScreenNotNow"
                            :class="platform == 'desktop' && !state.canPrompt ? 'btn-primary' : 'btn-light'"
                            @click="notNow">{{ platform == 'desktop' || forced ? 'סגירה' : 'לא עכשיו' }}</button>
                </div>

            </template>

        </div>

    </div>

</template>

<script>

import { homeScreenState, devicePlatform, iosBrowserIsSafari, shouldOfferAfterLogin, snooze, setOfferAfterLogin, markInstalled, promptInstall, registerServiceWorker } from '@/modules/HomeScreenInstall';
import { isHostingStationUser } from '@/core/acl/venue';
import hasPermission from '@/core/acl/acl';

// after the app is up
const FIRST_SHOW_MS = 3000;
const RETRY_MS = 1500;
// a screen that stays busy this long (~2 minutes): the next load
const MAX_TRIES = 80;
// the user just tapped / typed: wait for this much stillness
const IDLE_MS = 1200;
// Android: the browser's one-tap install usually becomes available a moment after login
const ANDROID_PROMPT_WAIT_MS = 8000;
// a tap that was already on its way when the popup came in does not close it
const GRACE_MS = 800;

// what the popup must not cover: an open modal, an alert, an announcement, the frozen-account cover
function screenIsBusy() {

    if (document.querySelector('.modal.show'))
        return true;

    if (document.querySelector('.swal2-container.swal2-shown'))
        return true;

    if (document.querySelector('.puAnnOverlay'))
        return true;

    // a presentation the customer is looking at (מצב תצוגה ללקוח)
    if (document.querySelector('.customerPresentation, .inventoryPresentation'))
        return true;

    return !!document.querySelector('.accountFrozenModal');

}

export default {

    name: 'HomeScreenPrompt',

    data() {
        return {
            open: false,
            leaving: false,
            // 'intro' (the offer) | 'steps' (how, on this device)
            mode: 'intro',
            // opened from the settings: closing it is not a "not now"
            forced: false,
            installing: false,
            platform: devicePlatform(),
            state: homeScreenState,
            mountedAt: Date.now(),
            shownAt: 0,
            lastInputAt: 0,
            backdropDown: false,
            timers: [],
        }
    },

    computed: {

        canSeeSettings() {
            return hasPermission(this.$store.state.user, 'settings.view');
        },

        steps() {

            if (this.platform == 'ios') {
                return [
                    {
                        title: 'לחצו על כפתור השיתוף',
                        icon: 'fa-solid fa-arrow-up-from-bracket',
                        sub: iosBrowserIsSafari() ? 'בסרגל של ספארי (אם אין שם כזה: ⋯ ואז "שיתוף")' : 'בשורת הכתובת של הדפדפן',
                    },
                    {
                        title: 'בחרו "הוספה למסך הבית"',
                        icon: 'fa-regular fa-square-plus',
                        sub: 'לא רואים? גללו מטה ברשימה',
                    },
                    {
                        title: 'לחצו "הוסף" - וזהו',
                        sub: 'האייקון של PlanUp יופיע במסך הבית',
                    },
                ];
            }

            if (this.platform == 'android') {
                return [
                    {
                        title: 'פתחו את תפריט הדפדפן',
                        icon: 'fa-solid fa-ellipsis-vertical',
                        sub: 'שלוש הנקודות בפינה (בדפדפן של סמסונג: ☰ למטה)',
                    },
                    {
                        title: 'בחרו "הוספה למסך הבית" או "התקנה"',
                        icon: 'fa-regular fa-square-plus',
                        sub: 'השם משתנה מעט מדפדפן לדפדפן',
                    },
                    {
                        title: 'אשרו - וזהו',
                        sub: 'האייקון של PlanUp יופיע במסך הבית',
                    },
                ];
            }

            return [
                {
                    title: 'בטלפון או בטאבלט: היכנסו ל-' + window.location.host,
                    icon: 'fa-solid fa-mobile-screen-button',
                    sub: 'החלון הזה יופיע שם אחרי הכניסה, ותמיד אפשר גם מההגדרות',
                },
                {
                    title: 'במחשב: אפשר להתקין כאפליקציה',
                    icon: 'fa-solid fa-display',
                    sub: 'בכרום ובאדג\' - סמל ההתקנה בשורת הכתובת. בספארי במק - קובץ ← הוספה ל-Dock',
                },
            ];

        },

    },

    mounted() {

        registerServiceWorker();

        this.$root.$on('openHomeScreenPrompt', this.openFromSettings);

        //the last tap / key anywhere in the app - the popup waits for a pause
        document.addEventListener('pointerdown', this.onUserInput, { capture: true, passive: true });
        document.addEventListener('keydown', this.onKeydown, true);

        if (this.autoOfferAllowed())
            this.later(() => this.showWhenQuiet(0), FIRST_SHOW_MS);

    },

    beforeDestroy() {

        this.$root.$off('openHomeScreenPrompt', this.openFromSettings);

        document.removeEventListener('pointerdown', this.onUserInput, { capture: true });
        document.removeEventListener('keydown', this.onKeydown, true);

        this.timers.forEach(timer => clearTimeout(timer));

    },

    methods: {

        later(fn, ms) {
            this.timers.push(setTimeout(fn, ms));
        },

        onUserInput() {
            this.lastInputAt = Date.now();
        },

        onKeydown(event) {

            this.lastInputAt = Date.now();

            if (this.open && (event.key == 'Escape' || event.key == 'Esc') && Date.now() - this.shownAt > GRACE_MS)
                this.notNow();

        },

        autoOfferAllowed() {

            let state = this.$store.state;
            let user = state.user;

            if (!state.isLogged || !user || user.type == 1 || isHostingStationUser(user) || state.accountFrozen)
                return false;

            //a new account's first load belongs to the starter kit popup - one popup at a time
            if (state.starterKit && state.starterKit.prompt)
                return false;

            return shouldOfferAfterLogin();

        },

        showWhenQuiet(tries) {

            if (this.open || !this.autoOfferAllowed())
                return;

            let busy = screenIsBusy() || Date.now() - this.lastInputAt < IDLE_MS;
            let waitForPrompt = this.platform == 'android' && !this.state.canPrompt && Date.now() - this.mountedAt < ANDROID_PROMPT_WAIT_MS;

            if (busy || waitForPrompt) {
                if (tries < MAX_TRIES)
                    this.later(() => this.showWhenQuiet(tries + 1), RETRY_MS);
                return;
            }

            this.forced = false;
            this.show('intro');

        },

        //the settings section: 'steps' (the browser has no dialog to open) or 'intro'
        openFromSettings(mode) {
            this.forced = true;
            this.show(mode == 'intro' ? 'intro' : 'steps');
        },

        show(mode) {

            this.mode = mode;
            this.leaving = false;
            this.installing = false;
            this.backdropDown = false;
            this.open = true;
            this.shownAt = Date.now();

            this.$nextTick(() => {
                if (this.$refs.card)
                    this.$refs.card.focus({ preventScroll: true });
            });

        },

        close() {

            if (!this.open || this.leaving)
                return;

            this.leaving = true;

            this.later(() => {
                this.open = false;
                this.leaving = false;
            }, 200);

        },

        add() {

            if (!this.state.canPrompt) {
                this.mode = 'steps';
                return;
            }

            this.installing = true;

            promptInstall().then(outcome => {

                this.installing = false;

                if (outcome == 'accepted') {
                    this.close();
                    this.notifyAdded();
                } else if (outcome == 'dismissed') {
                    this.notNow();
                } else {
                    this.mode = 'steps';
                }

            });

        },

        //"הוספתי" after the steps
        done() {
            markInstalled();
            this.close();
            this.notifyAdded();
        },

        notNow() {

            if (!this.forced)
                snooze();

            this.close();

        },

        never() {
            setOfferAfterLogin(false);
            this.close();
        },

        notifyAdded() {
            this.$notify({ type: 'success', title: 'PlanUp נוספה למסך הבית', duration: 3000 });
        },

        onBackdropMousedown(event) {
            this.backdropDown = event.target === event.currentTarget;
        },

        //only a press that started on the backdrop, and not the one that was on its way as the popup came in
        onBackdropClick() {

            if (!this.backdropDown || Date.now() - this.shownAt < GRACE_MS)
                return;

            this.backdropDown = false;
            this.notNow();

        },

    },

}

</script>

<style>
/* over the floating AI / support buttons (1050), under a bootstrap modal (1055), alerts and toasts */
.homeScreenPrompt {
    position: fixed;
    inset: 0;
    z-index: 1052;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    direction: rtl;
    background: rgba(24, 14, 52, .32);
    animation: homeScreenFade .25s ease both;
}

.homeScreenPrompt.leaving {
    animation: homeScreenFadeOut .2s ease both;
}

.homeScreenCard {
    position: relative;
    width: 100%;
    max-width: 400px;
    max-height: calc(100vh - 40px);
    overflow-y: auto;
    background: #fff;
    border-radius: 18px;
    padding: 20px 20px 16px;
    text-align: right;
    outline: none;
    box-shadow: 0 30px 70px -24px rgba(42, 19, 110, .55), 0 10px 24px -12px rgba(0, 0, 0, .25);
    animation: homeScreenIn .45s cubic-bezier(.16, 1, .3, 1) both;
}

.homeScreenPrompt.leaving .homeScreenCard {
    animation: homeScreenOut .2s ease both;
}

.homeScreenClose {
    position: absolute;
    top: 10px;
    left: 10px;
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #999;
    font-size: 16px;
    line-height: 32px;
    padding: 0;
}

.homeScreenClose:hover {
    background: #f3f0fa;
    color: #555;
}

.homeScreenHead {
    display: flex;
    align-items: center;
    gap: 14px;
}

.homeScreenAppIcon {
    flex: 0 0 auto;
    width: 56px;
    height: 56px;
    border-radius: 14px;
    border: 1px solid #ece8f5;
    box-shadow: 0 4px 12px -6px rgba(42, 19, 110, .35);
}

/* clear of the X */
.homeScreenTitle {
    padding-left: 26px;
    font-size: 17px;
    font-weight: 700;
    color: #2b2540;
    line-height: 1.3;
}

.homeScreenSub {
    margin-top: 3px;
    font-size: 14px;
    color: #6b6480;
    line-height: 1.4;
}

.homeScreenActions {
    display: flex;
    gap: 8px;
    margin-top: 18px;
}

.homeScreenActions .btn {
    flex: 1;
    white-space: nowrap;
    padding-top: 9px;
    padding-bottom: 9px;
}

.homeScreenActions .btn i {
    margin-left: 4px;
}

.homeScreenActions .homeScreenAdd,
.homeScreenActions .homeScreenDone {
    flex: 1.6;
}

.homeScreenFoot {
    margin-top: 12px;
    font-size: 13px;
    color: #999;
    text-align: center;
}

.homeScreenNever {
    color: #888;
    text-decoration: underline;
}

.homeScreenNever:hover {
    color: #555;
}

.homeScreenSettingsHint {
    margin-right: 4px;
}

.homeScreenSteps {
    list-style: none;
    margin: 16px 0 0;
    padding: 0;
}

.homeScreenStep {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px 0;
    border-top: 1px solid #f0edf6;
}

.homeScreenStep:first-child {
    border-top: 0;
}

.homeScreenStepNum {
    flex: 0 0 auto;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #f1ecfb;
    color: #7650c4;
    font-weight: 700;
    font-size: 14px;
    line-height: 26px;
    text-align: center;
}

.homeScreenStepText {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.homeScreenStepTitle {
    font-size: 15px;
    font-weight: 600;
    color: #2b2540;
    line-height: 1.45;
}

.homeScreenStepIcon {
    display: inline-block;
    margin-right: 6px;
    padding: 3px 7px;
    border-radius: 7px;
    background: #f1ecfb;
    color: #7650c4;
    font-size: 13px;
}

.homeScreenStepSub {
    margin-top: 2px;
    font-size: 13px;
    color: #7a7390;
    line-height: 1.45;
}

@keyframes homeScreenFade {
    from {
        opacity: 0;
    }
}

@keyframes homeScreenFadeOut {
    to {
        opacity: 0;
    }
}

@keyframes homeScreenIn {
    from {
        opacity: 0;
        transform: translateY(16px) scale(.98);
    }
}

@keyframes homeScreenOut {
    to {
        opacity: 0;
        transform: translateY(10px);
    }
}

@keyframes homeScreenSheetIn {
    from {
        transform: translateY(100%);
    }
}

/* phones: a bottom sheet */
@media (max-width: 576px) {

    .homeScreenPrompt {
        align-items: flex-end;
        padding: 0;
    }

    .homeScreenPrompt .homeScreenCard {
        max-width: 100%;
        max-height: 88vh;
        border-radius: 20px 20px 0 0;
        padding: 20px 18px calc(16px + env(safe-area-inset-bottom));
        animation: homeScreenSheetIn .4s cubic-bezier(.16, 1, .3, 1) both;
    }

}

@media (prefers-reduced-motion: reduce) {

    .homeScreenPrompt,
    .homeScreenPrompt .homeScreenCard {
        animation: none !important;
    }

}
</style>
